import type { TeactNode } from '../../lib/teact/teact';
import React from '../../lib/teact/teact';

import type { ApiChain } from '../../api/types';
import type { Account, AccountType } from '../../global/types';
import type { AccountBalance } from '../../hooks/useAccountsBalances';

import buildClassName from '../../util/buildClassName';
import { getOrderedAccountChains } from '../../util/chain';
import { formatAccountAddresses } from '../../util/formatAccountAddress';
import { formatCurrency } from '../../util/formatNumber';
import isViewAccount from '../../util/isViewAccount';

import SensitiveData from '../ui/SensitiveData';
import WalletAvatar from '../ui/WalletAvatar';

import styles from './AccountRowContent.module.scss';

export interface AccountRowInnerProps {
  accountId: string;
  byChain: Account['byChain'];
  visibleChains?: ApiChain[];
  accountType: AccountType;
  title?: string;
  isTestnet?: boolean;
  balanceData?: AccountBalance;
  isSensitiveDataHidden?: true;
  suffixIcon?: TeactNode;
  avatarClassName?: string;
}

/**
 * Renders just the inner content of an account row (avatar, info, balance, suffix).
 * Does NOT include a wrapper div - meant to be used inside parent's wrapper.
 */
function AccountRowInner({
  accountId,
  byChain,
  visibleChains,
  accountType,
  title,
  isTestnet,
  balanceData,
  isSensitiveDataHidden,
  suffixIcon,
  avatarClassName,
}: AccountRowInnerProps) {
  const isView = isViewAccount(accountType);
  const chains = visibleChains ?? getOrderedAccountChains(byChain);
  const formattedAddress = formatAccountAddresses(byChain, chains, chains.length === 1 ? 'medium' : 'small');

  return (
    <>
      <WalletAvatar
        title={title}
        accountId={accountId}
        className={buildClassName(styles.avatar, avatarClassName)}
      />

      <div className={styles.info}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{title}</span>
        </div>
        <div className={styles.address}>
          {isTestnet && <i className={buildClassName(styles.icon, 'icon-testnet')} aria-hidden />}
          {isView && <i className={buildClassName(styles.icon, 'icon-eye-filled')} aria-hidden />}
          {formattedAddress}
        </div>
      </div>

      {balanceData && (
        <SensitiveData
          isActive={isSensitiveDataHidden}
          rows={2}
          min={5}
          max={10}
          seed={title || ''}
          cellSize={8}
          className={styles.balanceWrapper}
          contentClassName={styles.balanceContainer}
          align="right"
        >
          <div className={styles.balance}>
            {formatCurrency(balanceData.value, balanceData.currencySymbol)}
          </div>
        </SensitiveData>
      )}

      {suffixIcon}
    </>
  );
}

export default AccountRowInner;
